import {
  Alert,
  Button,
  Group,
  List,
  Modal,
  Radio,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import {
  useIsMutating,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import {
  decommissionBot,
  type BotAccountProviderAction,
  type CredentialHolder,
  type DecommissionBotResponse,
} from "./decommissionApi";
import {
  credentialHoldersOf,
  describeDecommissionError,
} from "./decommissionErrors";

type TFn = ReturnType<typeof useTranslation>["t"];

// Ключ мутации забывания: пока запрос в полёте, модалку нельзя закрыть —
// закрытие не отменяет уже ушедший запрос.
function forgetMutationKey(username: string): readonly string[] {
  return ["connections", "forgejo", "decommission", username];
}

export interface ForgetBotModalProps {
  readonly username: string;
  readonly onClose: () => void;
  readonly onChanged: () => void;
  readonly onForgotten: (username: string) => void;
}

// Кого именно держит перечень: роль, владелец-учётка или неизвестность.
function holderSubject(
  holder: CredentialHolder,
  username: string,
  t: TFn,
): string {
  switch (holder.match) {
    case "source":
      return t("connections.tokens.botAccounts.holderMatch.source", {
        role:
          holder.role ?? t("connections.tokens.botAccounts.holderRoleUnknown"),
      });
    case "owner":
      return t("connections.tokens.botAccounts.holderMatch.owner", {
        account: username,
      });
    case "owner_unknown":
      return t("connections.tokens.botAccounts.holderMatch.owner_unknown");
    case "source_unknown":
      return t("connections.tokens.botAccounts.holderMatch.source_unknown");
  }
}

interface HoldersStepProps {
  readonly holders: readonly CredentialHolder[];
  readonly username: string;
  readonly pending: boolean;
  readonly onKeep: () => void;
  readonly onInterrupt: () => void;
}

// Шаг держателей: прерывание — выбор поведения, а не подтверждение
// (ADR-0065 § 6), поэтому отдельный шаг с двумя исходами.
function HoldersStep(props: HoldersStepProps): ReactElement {
  const { t } = useTranslation();
  const { holders, username, pending, onKeep, onInterrupt } = props;

  return (
    <Stack gap="sm">
      <Alert
        color="yellow"
        title={t("connections.tokens.botAccounts.holdersTitle")}
      >
        <Text size="sm">
          {t("connections.tokens.botAccounts.holdersIntro")}
        </Text>
        <List size="sm">
          {holders.map((holder) => (
            <List.Item
              key={JSON.stringify([
                holder.holder_kind,
                holder.match,
                holder.role,
                holder.account,
              ])}
            >
              {t("connections.tokens.botAccounts.holderLine", {
                kind: t(
                  `connections.tokens.botAccounts.holderKind.${holder.holder_kind}`,
                  { defaultValue: holder.holder_kind },
                ),
                subject: holderSubject(holder, username, t),
                amount: holder.count,
              })}
            </List.Item>
          ))}
        </List>
        <Text size="sm">
          {t("connections.tokens.botAccounts.holdersChoice")}
        </Text>
      </Alert>
      <Group justify="flex-end">
        <Button variant="default" disabled={pending} autoFocus onClick={onKeep}>
          {t("connections.tokens.botAccounts.holdersKeepButton")}
        </Button>
        <Button color="red" loading={pending} onClick={onInterrupt}>
          {t("connections.tokens.botAccounts.holdersInterruptButton")}
        </Button>
      </Group>
    </Stack>
  );
}

// Тело модалки. Монтируется только когда модалка открыта — чтобы
// confirm-input, выбор provider_action и шаг держателей сбрасывались
// между открытиями.
function ForgetBotModalBody(props: ForgetBotModalProps): ReactElement {
  const { t } = useTranslation();
  const { username, onClose, onChanged, onForgotten } = props;
  const queryClient = useQueryClient();

  const [confirmInput, setConfirmInput] = useState("");
  const [providerAction, setProviderAction] =
    useState<BotAccountProviderAction>("leave");
  const [actionError, setActionError] = useState<string | null>(null);
  // null — шаг подтверждения, массив — шаг держателей.
  const [holders, setHolders] = useState<readonly CredentialHolder[] | null>(
    null,
  );

  // Защита от случайного забывания живёт в интерфейсе (ADR-0065 § 9):
  // «Забыть» активна, только когда введённый логин совпал с username;
  // в запрос логин не уходит.
  const confirmMatches = confirmInput === username;

  const mutation = useMutation<DecommissionBotResponse, Error, boolean>({
    mutationKey: forgetMutationKey(username),
    mutationFn: (interruptHolders) =>
      decommissionBot(username, {
        provider_action: providerAction,
        interrupt_holders: interruptHolders,
      }),
    onSuccess: () => {
      setActionError(null);
      void queryClient.invalidateQueries({
        queryKey: ["connections", "forgejo", "tokens"],
      });
      onChanged();
      // Persistent success выводит секция: тихое закрытие у необратимого
      // действия — плохой UX.
      onForgotten(username);
      onClose();
    },
    onError: (err) => {
      const found = credentialHoldersOf(err);
      if (found !== null) {
        setHolders(found);
        setActionError(null);
        return;
      }
      setActionError(describeDecommissionError(err, t));
    },
  });

  return (
    <Stack gap="sm">
      {holders === null ? (
        <Text size="sm">{t("connections.tokens.botAccounts.confirmText")}</Text>
      ) : null}

      {actionError !== null ? (
        <Alert color="red" title={t("connections.errorHeading")}>
          {actionError}
        </Alert>
      ) : null}

      {holders === null ? (
        <>
          <TextInput
            label={t("connections.tokens.botAccounts.confirmInputLabel")}
            placeholder={t(
              "connections.tokens.botAccounts.confirmInputPlaceholder",
              { username },
            )}
            value={confirmInput}
            onChange={(event) => {
              setConfirmInput(event.currentTarget.value);
            }}
          />

          <Radio.Group
            label={t("connections.tokens.botAccounts.providerActionLabel")}
            value={providerAction}
            onChange={(value) => {
              setProviderAction(value);
            }}
          >
            <Stack gap="xs" mt="xs">
              <Radio
                value="leave"
                label={t("connections.tokens.botAccounts.providerActionLeave")}
              />
              <Radio
                value="block"
                label={t("connections.tokens.botAccounts.providerActionBlock")}
              />
              <Radio
                value="delete"
                label={t("connections.tokens.botAccounts.providerActionDelete")}
              />
            </Stack>
          </Radio.Group>

          {providerAction !== "leave" ? (
            <Alert color="yellow">
              {t("connections.tokens.botAccounts.providerActionWarning")}
            </Alert>
          ) : null}

          <Group justify="flex-end">
            <Button
              variant="default"
              disabled={mutation.isPending}
              onClick={onClose}
            >
              {t("connections.modalCancel")}
            </Button>
            <Button
              color="red"
              disabled={!confirmMatches}
              loading={mutation.isPending}
              onClick={() => {
                mutation.mutate(false);
              }}
            >
              {t("connections.tokens.botAccounts.forgetConfirmButton")}
            </Button>
          </Group>
        </>
      ) : (
        <HoldersStep
          holders={holders}
          username={username}
          pending={mutation.isPending}
          onKeep={onClose}
          onInterrupt={() => {
            mutation.mutate(true);
          }}
        />
      )}
    </Stack>
  );
}

// Modal с заголовком по username; тело монтируется только при открытии.
export function ForgetBotModal(props: ForgetBotModalProps): ReactElement {
  const { t } = useTranslation();
  const { username } = props;
  const busy = useIsMutating({ mutationKey: forgetMutationKey(username) }) > 0;

  return (
    <Modal
      opened
      onClose={props.onClose}
      withCloseButton={!busy}
      closeOnEscape={!busy}
      closeOnClickOutside={!busy}
      title={t("connections.tokens.botAccounts.confirmTitle", { username })}
      size="md"
      centered
    >
      <ForgetBotModalBody {...props} />
    </Modal>
  );
}
